"use client";

import { useMemo, useRef, useState } from "react";
import Button from "@/components/Button";
import DonationAmountInput from "@/components/DonationAmountInput";
import ProductDonation from "@/components/ProductDonation";
import ProductOptions from "@/components/ProductOptions";
import ProductQuantity from "@/components/ProductQuantity";
import StallContextBanner from "@/components/StallContextBanner";
import { donateButtonLabel, resolveMinAmount } from "@/helpers/amount";
import { filterStallOnlyFromOptions } from "@/helpers/giftStall";
import { ANALYTICS_EVENT, analytics } from "@/tools/analytics";
import { useCampaignContext } from "@/tools/analytics";
import { useCartActions, useStallContext } from "@/tools/medusa";
import type { CartLineItemMetadataInput } from "@/tools/medusa/types";

import type { IProductOption as ProductOption } from "@/tools/sanity/schema/documents/product";
import styles from "./styles.module.scss";

interface SanityVariant {
  _id: string;
  title: string;
  productVariantId: string;
  deliveryType?: string;
  variableAmount?: boolean;
  minAmount?: number;
  suggestedAmounts?: number[];
  options?: { raw?: string };
  [key: string]: any;
}

interface ProductSelectionProps {
  options: ProductOption[];
  variants: SanityVariant[];
  productId?: string;
  productTitle?: string;
  productPrice?: number;
  productType?: string;
}

const DEFAULT_SUGGESTED_AMOUNTS = [10, 25, 50, 100];

/** Find the variant that matches all selected option values. */
const findMatchingVariantFor = (
  variants: SanityVariant[],
  selections: Record<string, string>,
): SanityVariant | undefined => {
  if (Object.keys(selections).length === 0) {
    return variants[0];
  }
  return (
    variants.find((variant) => {
      if (!variant.options?.raw) {
        return false;
      }
      try {
        const parsed = JSON.parse(variant.options.raw) as { name: string; value: string }[];
        return Object.entries(selections).every(([optionName, optionValue]) =>
          parsed.some((o) => o.name === optionName && o.value === optionValue),
        );
      } catch {
        return false;
      }
    }) || variants[0]
  );
};

/**
 * Extracts valid option values for this product from its Sanity variants.
 * Each variant's `options.raw` is a JSON string like:
 * `[{ "name": "Card Type", "value": "Designed Card" }]`
 *
 * Returns a map: `{ "Card Type": Set(["Designed Card", "Colouring In"]) }`
 */
const getValidValuesFromVariants = (variants: SanityVariant[]): Map<string, Set<string>> => {
  const validValues = new Map<string, Set<string>>();

  for (const variant of variants) {
    if (!variant.options?.raw) {
      continue;
    }

    try {
      const parsed = JSON.parse(variant.options.raw) as { name: string; value: string }[];
      for (const { name, value } of parsed) {
        if (!validValues.has(name)) {
          validValues.set(name, new Set());
        }
        validValues.get(name)!.add(value);
      }
    } catch {
      continue;
    }
  }

  return validValues;
};

const ProductSelection = ({
  options,
  variants,
  productId,
  productTitle,
  productPrice,
  productType,
}: ProductSelectionProps) => {
  const [quantity, setQuantity] = useState(1);
  const [isDonationSelected, setIsDonationSelected] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  // The add buttons stay pressable while adding, so this stops a second press.
  // State alone lets a fast double press through before the re-render.
  const addInFlight = useRef(false);
  const [donationAmount, setDonationAmountCents] = useState<number | null>(null);
  const stallContext = useStallContext();
  const { addItem, addDonation, clearStallContext } = useCartActions();
  // Page-level campaign attribution from ProductTemplate's `<CampaignProvider>`.
  // Forwarded to the cart store as a fallback when no URL campaign is captured.
  const pageCampaignId = useCampaignContext();

  /** Find donation variant (custom fulfilment + variable amount enabled). */
  const donationVariant = useMemo(
    () => variants.find((v) => v.deliveryType === "custom_fulfilment" && v.variableAmount === true),
    [variants],
  );

  /** Read donation config directly from the variant's synced metadata. */
  const donationConfig = useMemo(() => {
    if (!donationVariant) {
      return null;
    }
    return {
      suggestedAmounts: donationVariant.suggestedAmounts ?? DEFAULT_SUGGESTED_AMOUNTS,
      minAmount: resolveMinAmount(donationVariant.minAmount),
    };
  }, [donationVariant]);

  /** Filter shared productOption values to only those used by this product's variants. */
  const filteredOptions = useMemo(() => {
    const base = variants.length === 0 ? options : undefined;
    const intersected =
      base ??
      (() => {
        const validValues = getValidValuesFromVariants(variants);
        return options
          .filter((option) => validValues.has(option.title))
          .map((option) => {
            const valid = validValues.get(option.title);
            return {
              ...option,
              values: (option.values ?? []).filter((v) => valid?.has(v.title)),
            };
          })
          .filter((option) => (option.values?.length ?? 0) > 0);
      })();

    // Hide stall-only option values unless the supporter is in a stall.
    return stallContext ? intersected : filterStallOnlyFromOptions(intersected);
  }, [options, variants, stallContext]);

  /** Auto-select the first value of each option on load. */
  const [selections, setSelections] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const option of filteredOptions) {
      const firstValue = option.values?.[0]?.title;
      if (firstValue) {
        initial[option.title] = firstValue;
      }
    }
    return initial;
  });

  const handleSelect = (optionTitle: string, valueTitle: string) => {
    setIsDonationSelected(false);
    const nextSelections = { ...selections, [optionTitle]: valueTitle };
    setSelections(nextSelections);

    if (productId && productTitle) {
      const variant = findMatchingVariantFor(variants, nextSelections);
      analytics.track({
        event: ANALYTICS_EVENT.SELECT_ITEM,
        payload: {
          product: {
            id: variant?.productVariantId || productId,
            title: productTitle,
            variantTitle: variant?.title,
            productType,
            category2: variant?.deliveryType,
            price: productPrice ?? 0,
          },
        },
      });
    }
  };

  const handleDonationClick = () => {
    setIsDonationSelected(true);
  };

  const allOptionsSelected = filteredOptions.every((opt) => selections[opt.title]);

  // "Donate now" until an amount is picked, then the amount itself — the PDP
  // donation is always a single gift, so there is no cadence to name.
  const donateLabel = donationAmount ? donateButtonLabel("Donate", donationAmount) : "Donate now";

  /** Find the variant that matches all selected option values. */
  const findMatchingVariant = () => findMatchingVariantFor(variants, selections);

  const handleAddToCart = async () => {
    if (addInFlight.current) {
      return;
    }
    if (isDonationSelected) {
      if (!donationVariant || !donationAmount) {
        return;
      }

      const variantId = donationVariant.productVariantId || donationVariant._id;
      if (!variantId) {
        return;
      }

      addInFlight.current = true;
      setIsAdding(true);
      try {
        // `product_type` marks this as a donation for the cart, which uses it
        // to suppress the quantity stepper. It was previously omitted here, so
        // a PDP donation reached the cart looking like a shop purchase.
        const metadata: CartLineItemMetadataInput = { product_type: "donation" };
        if (donationVariant.deliveryType) {
          metadata.delivery_type = donationVariant.deliveryType;
        }
        await addDonation(variantId, donationAmount, metadata, { pageCampaignId });
      } catch (error) {
        console.error("Failed to add donation:", error);
      } finally {
        addInFlight.current = false;
        setIsAdding(false);
      }
      return;
    }

    const selectedVariant = findMatchingVariant();
    const variantId = selectedVariant?.productVariantId || selectedVariant?._id;

    if (!variantId) {
      console.error("No variant ID found", {
        productId,
        productTitle,
        variantCount: variants.length,
        selectedVariant,
        selections,
      });
      return;
    }

    addInFlight.current = true;
    setIsAdding(true);
    try {
      await addItem(variantId, quantity, undefined, { pageCampaignId });
    } catch (error) {
      console.error("Failed to add to cart:", error);
    } finally {
      addInFlight.current = false;
      setIsAdding(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.optionsAndDonation}>
        {filteredOptions.length > 0 && (
          <div className={styles.options}>
            <ProductOptions
              options={filteredOptions}
              selections={isDonationSelected ? {} : selections}
              onSelect={handleSelect}
            />
          </div>
        )}

        {donationVariant && (
          <ProductDonation selected={isDonationSelected} onClick={handleDonationClick} />
        )}
      </div>

      {!isDonationSelected && (
        <div className={styles.actions}>
          <ProductQuantity value={quantity} onChange={setQuantity} />
          <Button
            text={variants.length === 0 ? "Currently unavailable" : "Add to cart"}
            theme="yellow"
            size="lg"
            onClick={handleAddToCart}
            // `aria-disabled` while adding, not `disabled`: a browser takes
            // focus off a button as it is disabled. `handleAddToCart` ignores
            // a press while one is in flight.
            disabled={variants.length === 0 || !allOptionsSelected}
            aria-disabled={isAdding || undefined}
            loading={isAdding}
            className={styles.addToCartButton}
          />
        </div>
      )}

      {isDonationSelected && (
        <>
          {donationConfig && (
            <DonationAmountInput
              suggestedAmounts={donationConfig.suggestedAmounts}
              minAmount={donationConfig.minAmount}
              value={donationAmount}
              onChange={setDonationAmountCents}
            />
          )}
          <div className={styles.actions}>
            <Button
              text={isAdding ? "Adding..." : donateLabel}
              theme="yellow"
              size="lg"
              onClick={handleAddToCart}
              disabled={!donationAmount}
              aria-disabled={isAdding || undefined}
              loading={isAdding}
              className={styles.addToCartButton}
            />
          </div>
        </>
      )}

      {stallContext && (
        <StallContextBanner stallContext={stallContext} onClear={clearStallContext} />
      )}
    </div>
  );
};

export default ProductSelection;
